import React, { useEffect, useState } from 'react'
import api from '../api'
import * as XLSX from 'xlsx'
import { Download, Search, Inbox, ClipboardList, CheckCircle2, Timer } from 'lucide-react'
import { Card, PageHeader, Button, Input, Select, Field, Badge, EmptyState } from '../components/ui'

const badgeTone = { SELESAI: 'success', PROSES: 'warning' }

// Ringkasan (2026-07-21) -- user harus hitung manual jumlah baris tiap kali filter (mis. filter
// status SELESAI lalu cocokkan ke slip payroll). data.length di sini SUDAH total hasil filter
// sesungguhnya (endpoint /pekerjaan tidak dipaginasi), jadi tinggal ditampilkan lebih jelas
// ketimbang subtitle kecil di PageHeader.
function KartuRingkasan({ icon: Icon, label, value, color, bg }) {
  return (
    <Card style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon size={18} color={color} strokeWidth={2} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', letterSpacing: -0.3 }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>{label}</div>
      </div>
    </Card>
  )
}

function formatJam(menit) {
  if (!menit) return '0 jam'
  const jam = Math.floor(menit / 60)
  const sisaMenit = menit % 60
  return sisaMenit ? `${jam} jam ${sisaMenit} mnt` : `${jam} jam`
}

export default function Rekap() {
  const [data, setData] = useState([])
  const [teknisiList, setTeknisiList] = useState([])
  const [jenisList, setJenisList] = useState([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState({
    dari: '', sampai: '', teknisi: '', jenis: '', status: '', lokasi: ''
  })

  useEffect(() => {
    const now = new Date()
    const dari = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
    const sampai = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`
    setFilter(f => ({ ...f, dari, sampai }))
    api.get('/pegawai').then(r => setTeknisiList(r.data))
    api.get('/jenis-pekerjaan').then(r => setJenisList(r.data))
  }, [])

  useEffect(() => { if (filter.dari) loadData() }, [filter.dari, filter.sampai])

  async function loadData() {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      Object.entries(filter).forEach(([k, v]) => { if (v) params.append(k, v) })
      const res = await api.get(`/pekerjaan?${params}`)
      setData(res.data)
    } finally { setLoading(false) }
  }

  function exportExcel() {
    const rows = data.map(d => ({
      Tanggal: d.tanggal,
      Teknisi: d.teknisi_nama || '-',
      Lokasi: d.lokasi,
      'Jenis Pekerjaan': d.jenis_pekerjaan_kode,
      Detail: d.detail_pekerjaan || '-',
      Status: d.status,
      'Waktu Mulai': d.waktu_mulai ? d.waktu_mulai.substring(11, 16) : '-',
      'Waktu Selesai': d.waktu_selesai ? d.waktu_selesai.substring(11, 16) : '-',
      'Durasi (mnt)': d.durasi_menit || '-',
      Catatan: d.catatan_terakhir || '-'
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Rekap')
    XLSX.writeFile(wb, `Rekap_${filter.dari}_${filter.sampai}.xlsx`)
  }

  function formatDurasi(menit) {
    if (!menit) return '-'
    if (menit < 60) return `${menit} mnt`
    return `${Math.floor(menit / 60)} jam ${menit % 60} mnt`
  }

  const totalSelesai = data.filter(d => d.status === 'SELESAI').length
  const totalProses = data.filter(d => d.status === 'PROSES').length
  const totalDurasiSelesai = data.reduce((sum, d) => sum + (d.status === 'SELESAI' ? (d.durasi_menit || 0) : 0), 0)

  return (
    <div>
      <PageHeader
        title="Rekap Pekerjaan"
        subtitle={`${data.length} data ditemukan`}
        actions={<Button variant="success" icon={Download} onClick={exportExcel}>Export Excel</Button>}
      />

      {/* Filter */}
      <Card style={{ padding: 16, marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <Field label="Dari">
          <Input type="date" value={filter.dari} onChange={e => setFilter({ ...filter, dari: e.target.value })} />
        </Field>
        <Field label="Sampai">
          <Input type="date" value={filter.sampai} onChange={e => setFilter({ ...filter, sampai: e.target.value })} />
        </Field>
        <Field label="Teknisi">
          <Select value={filter.teknisi} onChange={e => setFilter({ ...filter, teknisi: e.target.value })}>
            <option value="">Semua</option>
            {teknisiList.map(t => <option key={t.id} value={t.nama}>{t.nama}</option>)}
          </Select>
        </Field>
        <Field label="Jenis">
          <Select value={filter.jenis} onChange={e => setFilter({ ...filter, jenis: e.target.value })}>
            <option value="">Semua</option>
            {jenisList.map(j => <option key={j.id} value={j.kode}>{j.kode} - {j.nama}</option>)}
          </Select>
        </Field>
        <Field label="Status">
          <Select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}>
            <option value="">Semua</option>
            <option value="PROSES">PROSES</option>
            <option value="SELESAI">SELESAI</option>
          </Select>
        </Field>
        <Field label="Lokasi">
          <Input type="text" value={filter.lokasi} placeholder="Cari lokasi..." onChange={e => setFilter({ ...filter, lokasi: e.target.value })} />
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button icon={Search} onClick={loadData} style={{ width: '100%' }}>Cari</Button>
        </div>
      </Card>

      {/* Ringkasan hasil filter */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KartuRingkasan icon={ClipboardList} label="Total Pekerjaan" value={data.length} color="#4f46e5" bg="#eef2ff" />
        <KartuRingkasan icon={CheckCircle2} label="Selesai" value={totalSelesai} color="#16a34a" bg="#ecfdf3" />
        <KartuRingkasan icon={Timer} label="Proses" value={totalProses} color="#d97706" bg="#fffbeb" />
        <KartuRingkasan icon={Timer} label="Durasi (Selesai)" value={formatJam(totalDurasiSelesai)} color="#0ea5e9" bg="#f0f9ff" />
      </div>

      {/* Tabel */}
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafafc' }}>
                {['Tanggal', 'Teknisi', 'Lokasi', 'Jenis', 'Detail', 'Status', 'Mulai', 'Selesai', 'Durasi', 'Catatan'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ padding: 48, textAlign: 'center', color: 'var(--text-faint)' }}>Memuat...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={10}><EmptyState icon={Inbox} title="Tidak ada data" /></td></tr>
              ) : data.map((d, i) => (
                <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{d.tanggal}</td>
                  <td style={{ padding: '10px 16px', fontWeight: 500 }}>{d.teknisi_nama || '-'}</td>
                  <td style={{ padding: '10px 16px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.lokasi}</td>
                  <td style={{ padding: '10px 16px' }}><Badge tone="brand">{d.jenis_pekerjaan_kode}</Badge></td>
                  <td style={{ padding: '10px 16px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.detail_pekerjaan || '-'}</td>
                  <td style={{ padding: '10px 16px' }}><Badge tone={badgeTone[d.status] || 'neutral'}>{d.status}</Badge></td>
                  <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{d.waktu_mulai ? d.waktu_mulai.substring(11, 16) : '-'}</td>
                  <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{d.waktu_selesai ? d.waktu_selesai.substring(11, 16) : '-'}</td>
                  <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{formatDurasi(d.durasi_menit)}</td>
                  <td style={{ padding: '10px 16px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.catatan_terakhir || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
